import { IProductCart } from '@/shared/api/getProduct/types';
import { ICity } from '@/shared/api/getShops/types';
import { useCartStore } from '@/shared/store/cartStore';
import { useCityStore } from '@/shared/store/cityStore';
import { Portal } from '@/shared/ui/Portal';
import { getDateAfterDaysFormatted } from '@/shared/utils/getDateAfterDaysFormatted';
import { twMergeExt } from '@/shared/utils/twMergeExt';
import { wordEnding } from '@/shared/utils/wordEnding';
import FeaturesPopup from '@/widgets/FeaturesPopup/ui/FeaturesPopup';
import { DeliveryCard } from '@/widgets/product-tabs/ui/DeliveryAndPaymentTab/DeliveryCard';
import PvzMap from '@/widgets/PvzMap/ui/PvzMap';
import StoresMap from '@/widgets/StoresMap/ui/StoresMap';
import { cva } from 'class-variance-authority';
import { AnimatePresence } from 'framer-motion';
import { FC, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import DeliveryCourierIcon from '~/icons/delivery-courier.svg';
import DeliveryExpressIcon from '~/icons/delivery-express.svg';
import DeliveryPickupIcon from '~/icons/delivery-pickup.svg';
import DeliveryStoreIcon from '~/icons/delivery-store.svg';

interface Props {
  className?: string;
  data: IProductCart;
}

export const DeliveryAndPaymentTab: FC<Props> = ({ className, data }) => {
  const [showCourierPopup, setShowCourierPopup] = useState<boolean>(false);
  const [showExpressPopup, setShowExpressPopup] = useState<boolean>(false);
  const [showStoresMap, setShowStoresMap] = useState<boolean>(false);
  const [showPvzMap, setShowPvzMap] = useState<boolean>(false);
  const city = useCityStore((state) => state.city);

  const points = useCartStore(
    useShallow((state) => {
      return [...state.cdekPoints, ...state.boxberryPoints, ...state.russianPostPoints];
    })
  );
  const pvzCount = useCartStore((state) => {
    return (
      (state.cdekPoints?.length ?? 0) + (state.boxberryPoints?.length ?? 0) + (state.russianPostPoints?.length ?? 0)
    );
  });
  const cdekTariff = useCartStore((state) => state.cdekTariff);
  const deliveries = useCartStore((state) => state.deliveries);
  const isDeliveriesLoading = useCartStore((state) => state.isDeliveriesLoading);
  const cityName = useCityStore((state) => state.cityName);
  const cityHasShops = useCityStore((state) => state.cityHasShops);

  const cityShops = useMemo(() => {
    if (!city) return [];

    return data.amount.find((i) => i.city.code === city)?.store || [];
  }, [city]);

  const shopsAmount = useMemo(() => {
    if (!city) return [];

    const storesAvailable = cityShops.filter((i) => i.amount > 0) || [];

    if (storesAvailable.length > 0) {
      return [
        {
          name: cityName,
          coordinates: storesAvailable[0].store.map,
          shops: storesAvailable.map((i) => ({
            ...i.store,
            amount: i.amount,
          })),
        },
      ];
    }

    return [];
  }, [city]);

  const onClickCard = (type: string) => {
    if (type === 'courier') {
      setShowCourierPopup(true);
    } else if (type === 'express') {
      setShowExpressPopup(true);
    } else if (type === 'pickup') {
      setShowPvzMap(true);
    } else {
      setShowStoresMap(true);
    }
  };

  return (
    <div className={twMergeExt(cvaRoot(), className)}>
      <div className={'flex flex-col gap-6.4 flex-shrink-0 flex-grow-0 md:basis-[45.6rem]'}>
        <h3 className={'text-h3 font-secondary max-w-[25.1rem]'}>Доставка и&nbsp;оплата</h3>
      </div>

      <div className={'flex flex-col gap-2.4 flex-1 text-bodyL text-cBlack80'}>
        <div className={'flex flex-col gap-1.2'}>
          {shopsAmount?.length ? (
            <DeliveryCard
              iconSlot={<DeliveryStoreIcon />}
              title={'В наличии'}
              descr={
                cityName !== 'unknown'
                  ? `в ${shopsAmount[0].shops.length} ${wordEnding(shopsAmount[0].shops.length, [
                      'магазине',
                      'магазинах',
                      'магазинах',
                    ])}`
                  : ''
              }
              price={'Бесплатно'}
              date={cityName !== 'unknown' ? 'сегодня' : ''}
              onClick={() => onClickCard('stores')}
            />
          ) : null}
          {isDeliveriesLoading || pvzCount || cityName === 'unknown' ? (
            <DeliveryCard
              iconSlot={<DeliveryPickupIcon />}
              title={'Пункты выдачи'}
              descr={
                cityName !== 'unknown' ? `${pvzCount} ${wordEnding(pvzCount, ['пункт', 'пункта', 'пунктов'])}` : ''
              }
              price={`от ${'450'} ₽`}
              date={cityName !== 'unknown' ? getDateAfterDaysFormatted(cdekTariff?.period_max || 1) : ''}
              onClick={() => onClickCard('pickup')}
              isLoading={isDeliveriesLoading}
            />
          ) : null}
          {isDeliveriesLoading || (shopsAmount?.length && deliveries?.express && cityHasShops) ? (
            <DeliveryCard
              iconSlot={<DeliveryExpressIcon />}
              title={'Экспресс-доставка'}
              descr={deliveries?.express.price === 0 ? 'Бесплатно от 3000 ₽' : ''}
              price={isDeliveriesLoading ? '' : cityName !== 'unknown' ? `от ${deliveries!.express.price} ₽` : ''}
              date={cityName !== 'unknown' ? 'сегодня' : ''}
              isLoading={isDeliveriesLoading}
              onClick={() => onClickCard('express')}
            />
          ) : null}
          {isDeliveriesLoading || deliveries?.courier ? (
            <DeliveryCard
              iconSlot={<DeliveryCourierIcon />}
              title={'Доставка курьером'}
              price={cityName !== 'unknown' ? (isDeliveriesLoading ? '' : `${deliveries!.courier.price} ₽`) : ''}
              date={cityName !== 'unknown' ? 'в течение 3 дней' : ''}
              isLoading={isDeliveriesLoading}
              onClick={() => onClickCard('courier')}
            />
          ) : null}
        </div>
      </div>
      <AnimatePresence>
        {showCourierPopup ? (
          <Portal>
            <FeaturesPopup
              title={'Доставка курьером'}
              descr={'Курьерская доставка Boxberry'}
              icon={'/images/boxberry.svg'}
              features={[
                `Минимальная сумма заказа 900 рублей${cityName === 'Москва' ? ';' : '. Бесплатная доставка от 3 000 ₽;'}`,
                'Оплата на сайте. При заказе товаров можно списать бонусы;',
                'Смс и e-mail уведомления о статусах заказа;',
                'Для получения заказа может потребоваться паспорт.',
              ]}
              onClose={() => setShowCourierPopup(false)}
            />
          </Portal>
        ) : null}
      </AnimatePresence>
      <AnimatePresence>
        {showExpressPopup ? (
          <Portal>
            <FeaturesPopup
              title={'Экспресс доставка'}
              descr={'Курьерская доставка Яндекс'}
              icon={'/icons/yandex_icon2.png'}
              features={[
                `Минимальная сумма заказа 900 рублей${cityName === 'Москва' ? ';' : '. Бесплатная доставка от 3 000 ₽;'}`,
                'Оплата на сайте. При заказе товаров можно списать бонусы;',
                'Доставка с 10:00 до 20:00 в Москве внутри МКАД, Челябинске, Уфе, Сургуте, Екатеринбурге и Тюмени. 7 дней в неделю;',
                'Следите за перемещением курьера по карте в реальном времени в приложении Яндекс Go.',
              ]}
              onClose={() => setShowExpressPopup(false)}
            />
          </Portal>
        ) : null}
      </AnimatePresence>
      <AnimatePresence>
        {showStoresMap ? (
          <Portal>
            <StoresMap
              onClose={() => setShowStoresMap(false)}
              selectedProduct={data}
              activeCity={cityName}
              shops={shopsAmount as ICity[]}
              defaultProduct={[
                {
                  product: {
                    images: data.photos,
                  },
                },
              ]}
              typeI="availability"
            />
          </Portal>
        ) : null}
      </AnimatePresence>
      <AnimatePresence>
        {showPvzMap ? (
          <Portal>
            <PvzMap
              onClose={() => setShowPvzMap(false)}
              activeCity={cityName}
              shops={points}
              itemsCount={0}
              allItems={0}
              defaultProduct={[
                {
                  product: {
                    images: data.photos,
                  },
                },
              ]}
            />
          </Portal>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

const cvaRoot = cva(['DeliveryAndPaymentTab-cvaRoot', ' flex md:gap-[15.9rem] flex-col md:flex-row gap-2']);
